/** 
 * Core service for the chat with proper fetch-based streaming (bypasses browser buffering)
 * 
 * 📊 DEBUG LOGGING:
 * Data Analysis responses are logged to console with [DATA_ANALYSIS] prefix.
 * Complete raw response also saved to: window.__LAST_DATA_ANALYSIS_RAW_RESPONSE
 */
import { IPublicClientApplication } from '@azure/msal-browser';
import { getAccessTokenHelper } from './msalAuth';
import { filterStreamContent } from './streamParser';
import { normalizeTextForAPI } from './textNormalizer';
import logger from './logger';

// Types for the chat API
export interface ChatContentItem {
  type: 'input_file' | 'input_text' | 'input_image';
  filename?: string;
  file_data?: string; // data:mime;base64,<payload> (for input_file)
  image_url?: string; // data:mime;base64,<payload> (for input_image)
  text?: string; // for input_text
}

export interface ChatMessage {
  id?: string; // unique identifier for React key tracking
  role: 'user' | 'assistant' | 'system';
  content: string | ChatContentItem[];
  /**
   * The model that generated this assistant message. Null for RAGs, only set for assistant messages.
    * Regular chat: "GPT-5.1", "Claude Opus 4.7"
   * Reasoning: "GPT-5.1", "Claude Opus 4.6"
   * Web search: "Amazon Nova Lite"
   * Other regular chat tools: "GPT-5.1"
   * RAGs: null
   */
  model?: string | null;
  /** The model ID (from ModelConfig.id) used to generate this message. Reliable for programmatic checks. */
  modelId?: string | null;
  /**
   * The tool used to render this message (e.g., "regular_chat", "reasoning", "web_search", etc.)
   */
  tool?: string;
  tools?: string[]; // tool(s) active when this message was sent (for filtering)
  reasoning?: string; // optional reasoning trace (captured separately when reasoning tool active)
  thinking?: string; // optional thinking trace (captured separately when web_search tool active)
  isError?: boolean; // marks assistant error messages that can be retried
  isStopped?: boolean; // marks assistant messages that were stopped by user and can be retried
  citations?: any[]; // optional citations for RAG responses
  followUps?: string[]; // optional follow-up suggestion questions from RAG responses
  sources?: Array<{ url: string; title: string; snippet?: string }>; // optional sources for SourcesList component
  logs?: any[]; // optional logs for JIRA responses
  [key: string]: any; // Allow additional properties
  failedUserMessage?: ChatMessage; // original user message context for retry (only on error assistant messages)
  translationData?: Array<{ // optional translation file data for download buttons
    filename: string;
    content_base64: string;
    language: string;
  }>;
}

export interface ChatRequest {
  request_id: string;
  session?: string; // optional session identifier
  timestamp: string;
  metadata: {
    source: string;
  };
  query_type: string;
  model?: string; 
  chat_history: ChatMessage[];
  tools?: string[]; // Optional tools array
  // When true, backend should continue generating a normal response even if it would otherwise
  // instruct the client to switch to a RAG experience (e.g., Legal, HR).
  continue_llm_response?: boolean;
  // Identifies the RAG type that was suggested but declined (sent together with continue_llm_response).
  rag_type?: 'la' | 'hr';
  // For non-analysis endpoints we still support legacy 'files' objects.
  files?: Array<{ filename: string; content_base64?: string; file_id?: string }>;
  // NEW: For data_analysis endpoint we must send a flat array of file_ids ONLY (no filenames/base64)
  file_ids?: string[];
}

// For fetch stream
export interface ChatStreamChunk {
  delta?: {
    content?: string; // incremental text
  };
  content?: string; // direct content property
  data?: {
    content?: string; // content nested in data
  };
  finish_reason?: string; // normal, hit limit, etc
  [key: string]: any; // timestamp, model, id, etc
}

export class ChatService {
  private static readonly API_BASE_URL = process.env.REACT_APP_APIM_BASE_URL
    ? `${process.env.REACT_APP_APIM_BASE_URL}/nav-api/api/ai`
    : 'https://navigator.ai.3m.com/nav-api/api/ai';
  private static readonly MAX_RETRIES = 3;
  private static readonly RETRY_DELAY_BASE = 1000; // 1 second base delay
  private msalInstance: IPublicClientApplication;
  // Development flag (resolved at build time by many bundlers); fallback to runtime check
  private static readonly IS_DEV = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production');
  // Runtime debug toggle: set localStorage CHAT_DEBUG = 'true' (persists across reloads)
  private static isDebug(): boolean {
    try {
      if (ChatService.IS_DEV) return true;
      if (typeof window !== 'undefined' && window.localStorage.getItem('CHAT_DEBUG') === 'true') return true;
      return false;
    } catch { return ChatService.IS_DEV; }
  }
  
  // TEST MODE: Enable to simulate quota_exceeded errors
  private static TEST_QUOTA_ERROR = false;
  
  public static enableQuotaErrorTest() {
    ChatService.TEST_QUOTA_ERROR = true;
    
    // Store in window for debugging
    (window as any).__TEST_QUOTA_ERROR = true;
  }
  
  public static disableQuotaErrorTest() {
    ChatService.TEST_QUOTA_ERROR = false;
    (window as any).__TEST_QUOTA_ERROR = false;
  }
  
  public static getTestModeStatus() {
    logger.log('Current TEST_QUOTA_ERROR:', ChatService.TEST_QUOTA_ERROR);
    logger.log('Window backup:', (window as any).__TEST_QUOTA_ERROR);
    return ChatService.TEST_QUOTA_ERROR;
  }

  // Unified resilient logger that preserves messages even if logger.log is stripped or suppressed.
  // Stores log entries in window.__CHAT_LOGS (for developers to inspect) and uses console.info for visibility.
  // Large payloads should be truncated before calling this method.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static log(tag: string, data?: any): void {
    try {
      if (typeof window !== 'undefined') {
        // Maintain a rolling buffer (cap at 500 entries to avoid memory bloat)
        const w: any = window as any;
        w.__CHAT_LOGS = w.__CHAT_LOGS || [];
        w.__CHAT_LOGS.push({ ts: Date.now(), tag, data });
        if (w.__CHAT_LOGS.length > 500) {
          w.__CHAT_LOGS.splice(0, w.__CHAT_LOGS.length - 500);
        }
      }
      // Use info to reduce bundler removal risk versus logger.log
      // eslint-disable-next-line no-console
      console.info(tag, data);
    } catch {/* ignore logging failures */}
  }

  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }


  /**
   * Get the access token for API requests (delegates to shared helper)
   */
  private async getAccessToken(): Promise<string> {
    return getAccessTokenHelper(this.msalInstance);
  }

  /**
   * Generate a unique request ID
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Check if an HTTP status code is retryable
   */
  private isRetryableError(status: number): boolean {
    // Retry on server errors (5xx) and temporary client errors
    // DON'T retry on 401 (auth failure) or 403 (forbidden) - these won't fix themselves
    return status >= 500 || status === 429 || status === 408;
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  private getRetryDelay(retryCount: number): number {
    return ChatService.RETRY_DELAY_BASE * Math.pow(2, retryCount);
  }

  /**
   * Promise-based delay utility
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Create a chat request payload
   */
  private createChatRequest(
    chatHistory: ChatMessage[],
    queryType: string = 'response-api',
    tools?: string[],
    sessionId?: string,
    files?: Array<{ filename: string; content_base64?: string; file_id?: string }>,
    continueLlmResponse?: boolean,
    model?: string,
    ragType?: 'la' | 'hr'
  ): ChatRequest {
    // Normalize chat history content and exclude large image data to avoid API payload issues
    let normalizedChatHistory = chatHistory.map(message => {
      // If content is an array (structured user input with files/images), keep it as-is for backend
      if (Array.isArray(message.content)) {
        // Check if this contains actual file/image data (not just text)
        const hasFileData = message.content.some(it => 
          (it.type === 'input_file' && it.file_data) || 
          (it.type === 'input_image' && it.image_url)
        );
        
        if (hasFileData) {
          // Keep structured format for files/images with data
          return message;
        } else {
          // Convert to string if no file/image data (legacy behavior)
          const textParts: string[] = [];
          message.content.forEach(it => {
            if (it.type === 'input_text' && it.text) {
              textParts.push(normalizeTextForAPI(it.text));
            } else if (it.type === 'input_file' && it.filename) {
              textParts.push(`[Attached file: ${it.filename}]`);
            } else if (it.type === 'input_image') {
              textParts.push(`[Attached image]`);
            }
          });
          const content = textParts.join(' ');
          return { ...message, content } as ChatMessage;
        }
      }
      let content = message.content as string;
      
      // If this message contains generated images, replace with a text summary instead of including huge base64 data
      if (content.includes('![Generated Image](data:image/png;base64,')) {        
        // Try to preserve context about what was generated by looking at previous user messages
        // IMPORTANT: Only look at the current conversation session (the chatHistory passed to this function)
        
        // Look at ALL user messages in the current conversation to understand cumulative context
        // IMPORTANT: Only look at the current conversation session (the chatHistory passed to this function)
        // Filter out system instructions - be very specific to avoid filtering real user messages
        const allUserMessages = chatHistory
          .filter(msg => msg.role === 'user')
          .map(msg => {
            if (Array.isArray(msg.content)) {
              // Concatenate any input_text parts for heuristic analysis
              return msg.content.filter(ci => ci.type === 'input_text' && ci.text).map(ci => ci.text || '').join(' ');
            }
            return msg.content as string;
          })
          .filter(txt => txt && !txt.includes('CRITICAL IMAGE MODIFICATION INSTRUCTION') && !txt.startsWith('MODIFY the'))
          .map(txt => txt.toLowerCase());
                    
        // Just use generic "an image" for the base context to avoid payload bloat
        
        // Track any modifications mentioned in subsequent messages (skip the first message)
        // IMPORTANT: Only track modifications that have already been applied, not the current request
        const modifications = [];
        for (let i = 1; i < allUserMessages.length - 1; i++) { // -1 to exclude current message being processed
          const message = allUserMessages[i];
          
          // Look for modification keywords
          if (message.includes('remove') || message.includes('delete') || message.includes('take away') || message.includes('without')) {
            // Extract what should be removed
            const removeMatch = message.match(/(?:remove|delete|take away|without)\s+(?:the\s+)?([^.]+)/);
            if (removeMatch) {
              modifications.push(`removed ${removeMatch[1].trim()}`);
            }
          }
          
          if (message.includes('add') || message.includes('put') || message.includes('include')) {
            // Extract what should be added
            const addMatch = message.match(/(?:add|put|include)\s+(?:a\s+|an\s+|the\s+)?([^.]+)/);
            if (addMatch) {
              modifications.push(`added ${addMatch[1].trim()}`);
            }
          }
          
          if (message.includes('change') || message.includes('modify') || message.includes('make it')) {
            // Extract what should be changed
            const changeMatch = message.match(/(?:change|modify|make it)\s+([^.]+)/);
            if (changeMatch) {
              modifications.push(`modified ${changeMatch[1].trim()}`);
            }
          }
        }
        
        // Build cumulative context - keep it concise
        
        // Create a more descriptive context message that references only the ORIGINAL image, not cumulative modifications
        // CRITICAL: Never mention modifications in assistant messages - this confuses the AI into thinking they're already applied
        // if (afterImageText && afterImageText.toLowerCase().includes('adjustments')) {
        //   content = `I generated ${baseImageContext} based on your request. The user may want to make modifications or adjustments to this specific image I created.`;
        // } else {
        content = `I generated an image for you.`; // for now just always say image was generated, in future may need to change when we allow for modifications

      }
      
      return {
        ...message,
        content: normalizeTextForAPI(content)
      } as ChatMessage;
    });

    // Strip embedded file/image binaries from all user messages EXCEPT the most recent one.
    // The chat input keeps uploaded files attached across multiple sends, so each new
    // user message re-embeds whatever is currently attached. To avoid sending stale or
    // duplicate large payloads (e.g. a multi-MB PDF that was later removed and replaced
    // with a different file), only the latest user turn carries the actual binaries.
    // Earlier turns keep the filename reference so the model still sees the attachment.
    try {
      let latestUserIdx = -1;
      for (let i = normalizedChatHistory.length - 1; i >= 0; i--) {
        if (normalizedChatHistory[i].role === 'user') { latestUserIdx = i; break; }
      }
      for (let i = 0; i < normalizedChatHistory.length; i++) {
        if (i === latestUserIdx) continue;
        const msg = normalizedChatHistory[i];
        if (msg.role !== 'user' || !Array.isArray(msg.content)) continue;
        let mutated = false;
        const newContent = msg.content.map(item => {
          if (item.type === 'input_file' && item.file_data) {
            mutated = true;
            const { file_data, ...rest } = item;
            return rest as ChatContentItem;
          }
          if (item.type === 'input_image' && item.image_url) {
            mutated = true;
            const { image_url, ...rest } = item;
            return rest as ChatContentItem;
          }
          return item;
        });
        if (mutated) {
          normalizedChatHistory[i] = { ...msg, content: newContent };
        }
      }
    } catch {/* ignore strip failures — fall back to original payload */}

    // System message injection removed - files are now sent in structured content format directly in chat history
    // (Only used for data_analysis tool with file_ids)

    const request: ChatRequest = {
      request_id: this.generateRequestId(),
      ...(sessionId ? { session: sessionId } : {}),
      timestamp: new Date().toISOString(),
      metadata: {
        source: 'react-app'
      },
      query_type: queryType,
      ...(model ? { model } : {}),
      chat_history: normalizedChatHistory
    };

    if (continueLlmResponse) {
      request.continue_llm_response = true;
    }

    if (ragType) {
      request.rag_type = ragType;
    }

    // Add tools if provided
    if (tools && tools.length > 0) {
      request.tools = tools;
    }
    // SPECIAL CASE: data_analysis must use 'file_ids' array instead of 'files'
    if (tools?.includes('data_analysis')) {
      if (files && files.length > 0) {
        const ids = files.map(f => f.file_id).filter((id): id is string => !!id);
        if (ids.length > 0) {
          request.file_ids = ids;
        }
      }
    } else if (files && files.length > 0) {
      // Non-analysis flows keep legacy files array
      request.files = files;
    }
    // DEBUG: Log file attachment intent (sanitized head of each base64) for general chat (exclude image_generation to avoid large payload noise)
    try {
      if (!tools?.includes('image_generation') && files && files.length > 0) {
        // Always log file attachment summary (even for data_analysis for troubleshooting) but truncate base64 head.
        ChatService.log('[chat][createChatRequest][files]', files.map(f => ({ filename: f.filename, head: (f.content_base64||'').slice(0,40), len: (f.content_base64||'').length, file_id: f.file_id })));
      }
    } catch {/* ignore */}

    // EXTRA: Explicit raw payload logging for general chat with files BEFORE returning (helps verify base64 inclusion)
    try {
      if (request.files && request.files.length > 0) {
        const payloadStr = JSON.stringify(request);
        ChatService.log('[chat][raw-payload-length]', payloadStr.length);
        // Only emit head of payload if debug to avoid noise; still store in window logs.
        if (ChatService.isDebug()) {
          ChatService.log('[chat][raw-payload-head]', payloadStr.slice(0, 1200));
        }
        ChatService.log('[chat][files-base64-heads]', request.files.map(f => ({ filename: f.filename, base64_head: (f.content_base64||'').slice(0,140), base64_len: (f.content_base64||'').length })));        
      } else if (request.file_ids && request.file_ids.length > 0) {
        ChatService.log('[analysis][file-ids]', { count: request.file_ids.length, ids: request.file_ids });
      } else {
        // NEW: Structured user message file/image logging (images/files embedded in chat_history content arrays)
        try {
          const embeddedFiles: Array<{ type: string; filename?: string; head: string; len: number; index: number }> = [];
          request.chat_history.forEach((msg, idx) => {
            if (msg.role === 'user' && Array.isArray(msg.content)) {
              msg.content.forEach(ci => {
                if (ci.type === 'input_file' && ci.file_data) {
                  const base64Part = ci.file_data.split(',')[1] || '';
                  embeddedFiles.push({ type: 'file', filename: ci.filename || `file_${idx}`, head: base64Part.slice(0,140), len: base64Part.length, index: idx });
                } else if (ci.type === 'input_image' && ci.image_url) {
                  const base64Part = ci.image_url.split(',')[1] || '';
                  embeddedFiles.push({ type: 'image', head: base64Part.slice(0,140), len: base64Part.length, index: idx });
                }
              });
            }
          });
          if (embeddedFiles.length > 0) {
            ChatService.log('[chat][embedded-files]', embeddedFiles);
          }
        } catch (e) {
          // Logging suppressed
        }
      }
    } catch {/* ignore serialization errors */}

    return request;
  }

  /**
   * Fetch image generation quota for current authenticated user
   */
  public async getImageQuota(): Promise<{
    success: boolean;
    user_id?: string;
    quota?: {
      quota_type: string;
      current_usage: number;
      quota_limit: number;
      remaining: number;
      reset_time: string; // UTC ISO
      is_exceeded: boolean;
    }
  }> {
    const accessToken = await this.getAccessToken();
    const endpoint = `${ChatService.API_BASE_URL.replace('/api/ai','/api')}/quota/get-image-quota`;
    // Fallback if replace didn't work (different base path)
    const finalEndpoint = endpoint.includes('/quota/get-image-quota') ? endpoint : `${ChatService.API_BASE_URL}/quota/get-image-quota`;
    const resp = await fetch(finalEndpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json'
      }
    });
    logger.log(accessToken)
    if (!resp.ok) {
      throw new Error(`Image quota fetch failed (${resp.status})`);
    }
    try {
      return await resp.json();
    } catch {
      throw new Error('Invalid quota JSON response');
    }
  }

  /**
   * TEST METHOD: Simulate quota exceeded error for testing
   * Call this method to trigger the quota error flow without hitting the actual API
   */
  public async testQuotaError(): Promise<void> {
    // Simulate the error structure that comes from the API
    const mockErrorChunk = JSON.stringify({
      type: 'error',
      error_data: {
        code: 'quota_exceeded',
        message: 'Image generation quota exceeded. You can generate up to 3 images per 24 hours.',
        type: 'quota_error'
      }
    });
    
    // Simulate SSE format: data: {json}\n\n
    const sseChunk = `data: ${mockErrorChunk}\n\n`;
    
    // Create a mock readable stream that emits this error
    const mockStream = new ReadableStream({
      start(controller) {
        const encoder = new TextEncoder();
        controller.enqueue(encoder.encode(sseChunk));
        controller.close();
      }
    });
    
    // Process the mock stream through the same error detection logic
    const reader = mockStream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      
      // Parse the SSE event
      const match = buffer.match(/data: ({.*})\n\n/);
      if (match) {
        const parsed = JSON.parse(match[1]);
        
        if (parsed.error && parsed.error.code === 'quota_exceeded') {
          // Fetch quota data and create detailed error message
          try {
            const quotaResp = await this.getImageQuota();
            if (quotaResp.success && quotaResp.quota) {
              const { current_usage, quota_limit, reset_time } = quotaResp.quota;
              
              // Format reset time
              let formattedResetTime = 'tomorrow';
              try {
                const resetDate = new Date(reset_time);
                formattedResetTime = resetDate.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                });
              } catch {
                formattedResetTime = 'tomorrow';
              }
              
              throw new Error(`Image limit reached. You generated ${current_usage}/${quota_limit} images for the day. Your limit will reset at ${formattedResetTime}.`);
            }
          } catch (quotaError) {
            if (quotaError instanceof Error && quotaError.message.startsWith('Image limit reached')) {
              throw quotaError;
            }
          }
          
          throw new Error(parsed.error.message || 'Image generation quota exceeded. Please try again later.');
        }
      }
    }
  }

  /**
   * Sanitize a request payload for safe logging (avoid dumping large base64 blobs or sensitive tokens)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private sanitizeForLogging(request: ChatRequest): Record<string, any> {
    return {
      request_id: request.request_id,
      session: request.session,
      timestamp: request.timestamp,
      query_type: request.query_type,
      tools: request.tools,
      metadata: request.metadata,
      chat_history_summary: request.chat_history.map((m, idx) => ({
        idx,
        role: m.role,
        tools: m.tools,
        content_head: Array.isArray(m.content)
          ? m.content.map(ci => {
              if (ci.type === 'input_text') return ci.text || '';
              if (ci.type === 'input_file') return `[file:${ci.filename}]`;
              if (ci.type === 'input_image') return '[image]';
              return '';
            }).join(' | ').slice(0,200)
          : (m.content as string).slice(0, 200) + ((m.content as string).length > 200 ? '…' : '')
      })),
      files: request.files?.map(f => ({
        filename: f.filename,
        base64_length: f.content_base64 ? f.content_base64.length : 0,
        file_id: f.file_id
      })),
      file_ids: request.file_ids
    };
  }

  /**
   * Stream chat completion using fetch with ReadableStream for true character-by-character streaming
   * This bypasses browser buffering that occurs with axios/XMLHttpRequest
   */
  async streamChatCompletion(
    chatHistory: ChatMessage[],
    onChunk: (chunk: string) => void,
    onError: (error: Error) => void,
    onComplete: () => void,
    signal?: AbortSignal,
    retryCount: number = 0,
    tools?: string[],
    sessionId?: string,
    files?: Array<{ filename: string; content_base64?: string; file_id?: string }>,
    continueLlmResponse?: boolean,
    model?: string,
    queryTypeOverride?: string,
    ragType?: 'la' | 'hr'
  ): Promise<void> {
  // (Reasoning chunks now emitted incrementally as __reasoning JSON objects; no accumulation here.)
    try {
      const accessToken = await this.getAccessToken();      
      const isReasoning = tools?.includes('reasoning');
      const isDataAnalysis = tools?.includes('data_analysis');
      const isWebSearch = tools?.includes('web_search');
      // REQUIREMENT: For image generation requests we must strip prior chat history
      // and only send the most recent user prompt. This applies to both new image
      // creations and follow-up prompts; no previous assistant image or user text
      // should be included. (Product request 2025-11-04)
      // NOTE: History is now filtered upstream in useChatStream, but we keep this as defensive code.
      let effectiveChatHistory = chatHistory;
      if (tools?.includes('image_generation') && chatHistory.length > 1) {
        // Defensive: if somehow multiple messages passed, take only the last one
        effectiveChatHistory = chatHistory.slice(-1);
      }
      // // Modify chatHistory BEFORE creating the request payload if this is an image modification
      // let modifiedChatHistory = [...chatHistory];
    // if (isImageModification && chatHistory.length >= 2) {
      // (requestPayload created later after optional image modification logic)
        
      //   // Extract context about what was in the previous image by analyzing the entire conversation
      //   // IMPORTANT: Look at ALL user messages to understand cumulative modifications
        
      //   // Get all user messages in the conversation to understand cumulative context
      //   // Filter out system instructions - be very specific to avoid filtering real user messages
      //   const allUserMessages = chatHistory
      //     .filter(msg => msg.role === 'user' && 
      //       !msg.content.includes('CRITICAL IMAGE MODIFICATION INSTRUCTION') &&
      //       !msg.content.startsWith('MODIFY the image that was just generated:'))
      //     .map(msg => msg.content.toLowerCase());
        
      //   if (allUserMessages.length > 0) {
      //     // Keep it simple - just track cumulative modifications without base image details
      //     const previousModifications = [];
      //     for (let i = 1; i < allUserMessages.length - 1; i++) { // -1 to exclude current message
      //       const message = allUserMessages[i];

      //       // Track removals
      //       if (message.includes('remove') || message.includes('delete') || message.includes('take away') || message.includes('without')) {
      //         const removeMatch = message.match(/(?:remove|delete|take away|without)\s+(?:the\s+)?([^.]+)/);
      //         if (removeMatch) {
      //           previousModifications.push(`${removeMatch[1].trim()} removed`);
      //         }
      //       }
            
      //       // Track additions
      //       if (message.includes('add') || message.includes('put') || message.includes('include')) {
      //         const addMatch = message.match(/(?:add|put|include)\s+(?:a\s+|an\s+|the\s+)?([^.]+)/);
      //         if (addMatch) {
      //           previousModifications.push(`${addMatch[1].trim()} added`);
      //         }
      //       }
            
      //       // Track changes
      //       if (message.includes('change') || message.includes('modify') || message.includes('make it')) {
      //         const changeMatch = message.match(/(?:change|modify|make it)\s+([^.]+)/);
      //         if (changeMatch) {
      //           previousModifications.push(`${changeMatch[1].trim()} modified`);
      //         }
      //       }
      //     }
          
      //     // Build concise context
      //   }
                
      //   // Get the original image description from the first user message
      //   const originalImageDescription = allUserMessages.length > 0 ? allUserMessages[0] : 'the previous image';
        
      //   // Add a specific system-style instruction for image modification
      //   modifiedChatHistory.unshift({
      //     role: 'user' as const,
      //     content: `CRITICAL IMAGE MODIFICATION INSTRUCTION: The user wants to modify the image of "${originalImageDescription}" that was just generated. 
      //     **YOU MUST:**
      //     - Keep the EXACT same scene: ${originalImageDescription}
      //     - Keep the EXACT same composition, lighting, colors, style
      //     - Keep ALL existing elements EXCEPT what the user wants to change
      //     - This is IMAGE EDITING, not new image generation
      //     - DO NOT create a completely different scene or subject

      //     **MODIFICATION REQUESTED:** ${originalMessage}

      //     **IMPORTANT:** This is an edit to the existing "${originalImageDescription}" image. Preserve everything else exactly as it was.`
      //   });
        
      //   // Add enhancement instructions while preserving the original user message
      //   modifiedChatHistory.push({
      //     role: 'user' as const,
      //     content: `EDIT COMMAND: Take the "${originalImageDescription}" image and apply ONLY this change: ${originalMessage}. 

      //     DO NOT generate a new image. DO NOT change the scene. ONLY modify: ${originalMessage}

      //     Keep everything else identical - same lighting, same colors, same composition, same style.`
      //   });
      // }

      // Determine query_type from selected model's resolved config

      const requestPayload = this.createChatRequest(
        effectiveChatHistory,
        queryTypeOverride || 'response-api',
        tools && tools.length > 0 ? tools : undefined,
        sessionId,
        files,
        continueLlmResponse,
        model,
        ragType
      );

      // Reset stream parser state for new request
      filterStreamContent('', true);

      // TEST MODE: Simulate quota_exceeded error for image_generation
      if (ChatService.TEST_QUOTA_ERROR && tools?.includes('image_generation')) {
        ChatService.TEST_QUOTA_ERROR = false; // Reset after one use
        (window as any).__TEST_QUOTA_ERROR = false;
        ChatService.TEST_QUOTA_ERROR = false; // Reset after one use
        
        // Fetch quota data and throw error immediately
        const quotaResp = await this.getImageQuota();
        logger.log('🧪 Quota response:', quotaResp);
        
        if (quotaResp.success && quotaResp.quota) {
          const { current_usage, quota_limit, reset_time } = quotaResp.quota;
          
          let formattedResetTime = 'tomorrow';
          try {
            const resetDate = new Date(reset_time);
            formattedResetTime = resetDate.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
          } catch {
            formattedResetTime = 'tomorrow';
          }
          
          const errorMessage = `Image limit reached. You generated ${current_usage}/${quota_limit} images for the day. Your limit will reset at ${formattedResetTime}.`;
          throw new Error(errorMessage);
        }
        
        throw new Error('Image generation quota exceeded. Please try again later.');
      }
      
      const response = await fetch(
        `${ChatService.API_BASE_URL}/invoke-backend?stream=true`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestPayload),
          signal: signal,
        }
      );

      if (!response.ok) {
        // Try to read the error response body for detailed error information
        let errorMessage = 'Something went wrong or there is high load on our server. Please try again later.';
        
        try {
          const errorBody = await response.text();
          logger.log('[HTTP-ERROR-BODY]', { status: response.status, bodyPreview: errorBody.slice(0, 300) });
          // Try to parse as JSON first (SSE format or plain JSON)
          let parsed: any = null;
          
          // Handle SSE format: data: <json>\n\n
          // Use a greedy match from "data:" to end-of-line to capture nested JSON correctly
          const sseMatch = errorBody.match(/data:\s*(\{[\s\S]*?\})\s*(?:\n|$)/);
          if (sseMatch) {
            try {
              parsed = JSON.parse(sseMatch[1]);
            } catch {
              // Not valid JSON in SSE format — try grabbing everything after "data: "
              const dataIdx = errorBody.indexOf('data:');
              if (dataIdx !== -1) {
                try { parsed = JSON.parse(errorBody.slice(dataIdx + 5).trim()); } catch { /* ignore */ }
              }
            }
          }
          
          // Handle plain JSON
          if (!parsed) {
            try {
              parsed = JSON.parse(errorBody);
            } catch {
              // Not valid JSON
            }
          }
          
          logger.log('[HTTP-ERROR-PARSED]', { parsed });
          
          // Check for quota_exceeded error
          if (parsed?.error?.code === 'quota_exceeded') {
            // Fetch quota data for detailed error message
            try {
              const quotaResp = await this.getImageQuota();
              if (quotaResp.success && quotaResp.quota) {
                const { current_usage, quota_limit, reset_time } = quotaResp.quota;
                
                let formattedResetTime = 'tomorrow';
                try {
                  const resetDate = new Date(reset_time);
                  formattedResetTime = resetDate.toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  });
                } catch {
                  formattedResetTime = 'tomorrow';
                }
                
                errorMessage = `Image limit reached. You generated ${current_usage}/${quota_limit} images for the day. Your limit will reset at ${formattedResetTime}.`;
              } else {
                // Fallback to API message if quota fetch fails
                errorMessage = parsed.error.message || 'Image generation quota exceeded. Please try again later.';
              }
            } catch {
              // Use API error message if quota fetch fails
              errorMessage = parsed.error.message || 'Image generation quota exceeded. Please try again later.';
            }
          } else {
            // Handle both error shapes and sanitize the message for display:
            //   Shape 1: { error: { message: '...' } }
            //   Shape 2: { type: 'error', error_data: { message: '...' } }
            const rawMsg: string = parsed?.error?.message ?? parsed?.error_data?.message ?? '';
            if (rawMsg) {
              if (/rejected.*safety/i.test(rawMsg) || /safety.*rejected/i.test(rawMsg)) {
                errorMessage = 'Your request was rejected by the safety system. Please try rephrasing your prompt.';
              } else {
                // Strip noisy "If you believe this is an error, contact us at ... request ID ..." tail
                errorMessage = rawMsg.replace(/\s*If you believe this is an error[^.]*\./i, '').trim() || rawMsg;
              }
            }
          }
        } catch {
          // If we can't read the error body, use generic message
        }
        
        // Handle retryable errors (but not quota errors)
        const isQuotaError = errorMessage.includes('Image limit reached') || errorMessage.includes('quota exceeded');
        if (!isQuotaError && this.isRetryableError(response.status) && retryCount < ChatService.MAX_RETRIES) {
          await this.delay(this.getRetryDelay(retryCount));

          if (signal?.aborted) {
            throw new Error('Request aborted');
          }

          return this.streamChatCompletion(chatHistory, onChunk, onError, onComplete, signal, retryCount + 1, tools, sessionId, files, continueLlmResponse, model, queryTypeOverride);
        }

        // Throw the error message (either specific or generic)
        throw new Error(errorMessage);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
  // Removed image_generation streaming reader ready log per user request.
  // (Removed reasoning debug log: streaming reader ready)

      if (!reader) {
        throw new Error('No response body');
      }

  // Don't emit "Searching the web..." as actual content - it will be shown via initial state
  // If we emit it here, it gets queued and can't be properly cleared
  // The useChatStream hook sets the initial state with "Searching the web..." already

  // Accumulate text as small chunks arrive from ReadableStream
  let accumulatedText = '';
  const debugResponsePieces: string[] = [];
  let sseBuffer = ''; // only used for data_analysis event parsing
  // Image generation specific: SSE buffer for parsing structured JSON events
  let imageGenerationBuffer = '';

  // Separate reasoning variables / Line-based reasoning event handling
  let reasoningEventOpen = false;
  let reasoningLines: string[] = [];
  // Carry-over buffer for SSE lines split across network chunk boundaries (reasoning path)
  let reasoningLineCarryOver = '';

  // Web search specific: SSE buffer and citation tracking
  let webSearchBuffer = '';
  const webSearchCitations: Array<{url: string; domain: string}> = [];
  let webSearchContentStarted = false;
  const webSearchThinkingPieces: string[] = []; // Accumulate thinking content for dropdown
  let webSearchAccumulatedContent = ''; // Accumulate content to find complete thinking blocks
  // Generic error detection: accumulate chunks to detect complete error JSON
  let errorCheckBuffer = '';
      while (true) { // keep infinitely looping until done
        const { done, value } = await reader.read();
        
        // RAW CHUNK LOGGING FOR DEBUGGING STREAMING ISSUES
        // if (!done && value) {
        //   const chunk = decoder.decode(value, { stream: true });
        //   logger.log('[RAW-CHUNK]', chunk);
        // }
        
  if (done) {
          logger.log('[STREAM-DONE][PRE-FLUSH]', {
            accumulatedTextLength: accumulatedText.length,
            accumulatedTextPreview: accumulatedText.slice(0, 200),
            hasAccumulatedText: !!accumulatedText.trim(),
            isImageGen: tools?.includes('image_generation'),
            imageBufferLength: imageGenerationBuffer.length,
            timestamp: Date.now()
          });

          // FLUSH remaining imageGenerationBuffer — the final SSE event may not have a
          // trailing \n\n, leaving it unparsed. Process it exactly like the loop above.
          if (tools?.includes('image_generation') && imageGenerationBuffer.trim()) {
            logger.log('[STREAM-DONE][IMAGE-BUFFER-FLUSH]', { bufferLength: imageGenerationBuffer.length });
            const block = imageGenerationBuffer.trim();
            imageGenerationBuffer = '';
            const lines = block.split('\n');
            const dataLines: string[] = [];
            for (const line of lines) {
              if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim());
              }
            }
            const jsonStr = dataLines.join('\n');
            if (jsonStr && jsonStr !== '[DONE]') {
              try {
                const parsed = JSON.parse(jsonStr);
                if ((parsed.type === 'image_generation.partial_image' || parsed.type === 'image_generation.completed')
                    && parsed.image_data?.b64_json) {
                  const base64 = parsed.image_data.b64_json;
                  const imageMarkdown = `![Generated Image](data:image/png;base64,${base64})`;
                  logger.log('[STREAM-DONE][IMAGE-BUFFER-FLUSH-EVENT]', {
                    type: parsed.type,
                    base64Length: base64.length
                  });
                  onChunk(JSON.stringify({ type: 'replace', content: imageMarkdown }));
                }
              } catch (parseError) {
                console.warn('[STREAM-DONE][IMAGE-BUFFER-FLUSH-PARSE-ERROR]', {
                  jsonStrPreview: jsonStr.slice(0, 100),
                  error: parseError
                });
              }
            }
          }

          // RACE CONDITION BUG: Flush any remaining accumulated text before completing
          // The filterStreamContent function only returns new deltas, so we need to
          // give it one final chance to process any buffered content.
          // NOTE: For isReasoning, accumulatedText now holds extracted plain text (not SSE lines),
          // so we skip filterStreamContent and just emit it directly if non-empty.
          if (accumulatedText.trim()) {
            if (isReasoning) {
              // Content was already emitted token-by-token; no flush needed.
              // accumulatedText is plain text here — nothing to re-emit.
              logger.log('[STREAM-DONE][REASONING-NO-FLUSH-NEEDED]', { accumulatedTextLength: accumulatedText.length });
            } else {
            // flush out buffer before completing
            const finalContent = filterStreamContent(accumulatedText);
            logger.log('[STREAM-DONE][POST-FLUSH]', {
              finalContentLength: finalContent ? finalContent.length : 0,
              finalContentPreview: finalContent ? finalContent.slice(0, 200) : 'EMPTY',
              wasEmpty: !finalContent,
              timestamp: Date.now()
            });
            if (finalContent) {
                onChunk(finalContent); // send remaining content
            } else {
              console.warn('[STREAM-DONE][BUG] accumulatedText had content but filterStreamContent returned nothing!', {
                accumulatedTextLength: accumulatedText.length,
                accumulatedTextPreview: accumulatedText.slice(0, 500)
              });
            }
            }
          }
          
          onComplete(); // NOW call onComplete
          break;
    }

      // Decode bytes
      let chunk = decoder.decode(value, { stream: true });
      
        // (Removed reasoning debug log: raw_chunk content)

        // Special SSE/event parsing path for data_analysis tool
        if (isDataAnalysis) {
          sseBuffer += chunk;
          // Normalize line endings
          const delimiterRegex = /\n\n|\r\n\r\n/;
          let match: RegExpExecArray | null;
          // Manually extract complete event blocks separated by blank lines
          while ((match = delimiterRegex.exec(sseBuffer)) !== null) {
            const block = sseBuffer.slice(0, match.index).trim();
            sseBuffer = sseBuffer.slice(match.index + match[0].length);
            if (!block) continue;
            const lines = block.split(/\r?\n/);
            let eventType = 'message';
            const dataLines: string[] = [];
            for (const l of lines) {
              if (l.startsWith('event:')) eventType = l.slice(6).trim();
              else if (l.startsWith('data:')) dataLines.push(l.slice(5).trim());
            }
            const rawData = dataLines.join('\n');
            if (!rawData || rawData === '[DONE]') continue;
            // Log raw block BEFORE parsing for structural verification
            // Removed event-block raw logging.
            let parsed: any = null;
            try { 
              parsed = JSON.parse(rawData);
              
              // Check for error response (type: "error")
              if (parsed.error && parsed.error.code) {
                console.error('[data_analysis] Server error:', parsed.error);
                
                // Handle quota_exceeded error specifically
                if (parsed.error.code === 'quota_exceeded') {
                  // Fetch quota data and create detailed error message
                  try {
                    const quotaResp = await this.getImageQuota();
                    if (quotaResp.success && quotaResp.quota) {
                      const { current_usage, quota_limit, reset_time } = quotaResp.quota;
                      
                      // Format reset time
                      let formattedResetTime = 'tomorrow';
                      try {
                        const resetDate = new Date(reset_time);
                        formattedResetTime = resetDate.toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        });
                      } catch {
                        formattedResetTime = 'tomorrow';
                      }
                      
                      throw new Error(`Image limit reached. You generated ${current_usage}/${quota_limit} images for the day. Your limit will reset at ${formattedResetTime}.`);
                    }
                  } catch (quotaError) {
                    // If quota fetch fails, use the error message from API
                    if (quotaError instanceof Error && quotaError.message.startsWith('Image limit reached')) {
                      throw quotaError;
                    }
                  }
                  
                  // Fallback to generic quota error message
                  throw new Error(parsed.error.message || 'Image generation quota exceeded. Please try again later.');
                }
                
                // Throw generic error for other error types
                throw new Error('Something went wrong or there is high load on our server. Please try again later.');
              }
            } catch (parseError) {
              // If it's our thrown error, re-throw it
              if (parseError instanceof Error && parseError.message.includes('error occurred')) {
                throw parseError;
              }
              // If it's our server error message or quota error, re-throw it
              if (parseError instanceof Error && (parseError.message.includes('Something went wrong') || parseError.message.includes('Image limit reached'))) {
                throw parseError;
              }
              // Otherwise it's a JSON parse error, log and continue
              console.warn('[data_analysis] JSON parse error for chunk:', rawData.substring(0, 100));
            }
            const eventObj = { __analysis_event: true, eventType, raw: rawData, parsed };
            onChunk(JSON.stringify(eventObj));
          }
          // Skip generic parser until we have non-analysis tools
          continue;
        }

        // Special SSE/event parsing for web_search tool
        if (isWebSearch) {
          webSearchBuffer += chunk;
          // Parse SSE events (format: event: <type>\ndata: <json>\n\n)
          const eventDelimiter = /\n\n|\r\n\r\n/;
          let eventMatch: RegExpExecArray | null;
          
          while ((eventMatch = eventDelimiter.exec(webSearchBuffer)) !== null) {
            const block = webSearchBuffer.slice(0, eventMatch.index).trim();
            webSearchBuffer = webSearchBuffer.slice(eventMatch.index + eventMatch[0].length);
            
            if (!block) continue;
            
            const lines = block.split(/\r?\n/);
            const dataLines: string[] = [];
            
            for (const line of lines) {
              if (line.startsWith('event:')) {
                // Event type logged but not used for now
              } else if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim());
              }
            }
            
            const rawData = dataLines.join('\n');
            if (!rawData || rawData === '[DONE]') continue;
            
            try {
              const parsed = JSON.parse(rawData);
              
              // Check for error response
              if (parsed.error && parsed.error.code) {
                // Handle quota_exceeded error specifically
                if (parsed.error.code === 'quota_exceeded') {
                  try {
                    const quotaResp = await this.getImageQuota();
                    if (quotaResp.success && quotaResp.quota) {
                      const { current_usage, quota_limit, reset_time } = quotaResp.quota;
                      
                      let formattedResetTime = 'tomorrow';
                      try {
                        const resetDate = new Date(reset_time);
                        formattedResetTime = resetDate.toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        });
                      } catch {
                        formattedResetTime = 'tomorrow';
                      }
                      
                      throw new Error(`Image limit reached. You generated ${current_usage}/${quota_limit} images for the day. Your limit will reset at ${formattedResetTime}.`);
                    }
                  } catch (quotaError) {
                    if (quotaError instanceof Error && quotaError.message.startsWith('Image limit reached')) {
                      throw quotaError;
                    }
                  }
                  
                  throw new Error(parsed.error.message || 'Image generation quota exceeded. Please try again later.');
                }
                
                // Throw generic error for other error types
                throw new Error('Something went wrong or there is high load on our server. Please try again later.');
              }
              
              // Handle different event types (citation, tool, normal text)
              if (parsed.type === 'content' && parsed.data?.content) {
                let content = parsed.data.content;
                

                // Check if this is a citation
                const citationMatch = content.match(/Citation:\s*\{[^}]*'location':\s*\{[^}]*'web':\s*\{[^}]*'url':\s*'([^']+)'[^}]*'domain':\s*'([^']+)'[^}]*\}[^}]*\}[^}]*\}/);
                if (citationMatch) {
                  const [, url, domain] = citationMatch;
                  // Store citation but don't emit it as content
                  webSearchCitations.push({ url, domain });
                  continue; // Don't emit citation text
                }

                // Filter out tool call content (e.g. "Tool: {'input': '{\"query\":\"...\"}'}")
                if (/^Tool:\s*\{/.test(content)) {
                  continue; // Don't emit tool call text to the UI
                }

                // Check if this chunk contains thinking tags
                const hasThinkingTag = content.includes('<thinking') || content.includes('thinking>') || content.includes('</thinking');
                
                // Only accumulate content if we see thinking tags or if we have accumulated content with incomplete thinking
                if (hasThinkingTag || (webSearchAccumulatedContent && webSearchAccumulatedContent.includes('<thinking'))) {
                  webSearchAccumulatedContent += content;
                  
                  // Try to extract complete thinking blocks from accumulated content (handle malformed tags with spaces)
                  const thinkingMatches = webSearchAccumulatedContent.match(/<thinking>([\s\S]*?)<\/\s*thinking\s*>/gi);
                  if (thinkingMatches) {
                    thinkingMatches.forEach((match: string) => {
                      // Check if we've already captured this exact block
                      if (!webSearchThinkingPieces.includes(match)) {
                        // Remove the tags (including malformed ones with spaces) and extract just the content
                        const thinkingContent = match.replace(/<\/?thinking\s*>/gi, '').trim();
                        if (thinkingContent) {
                          webSearchThinkingPieces.push(match); // Store the full match to avoid duplicates
                          // Emit thinking as a special JSON object (similar to reasoning)
                          onChunk(JSON.stringify({ __web_search_thinking: true, content: thinkingContent }));
                        }
                      }
                    });
                    
                    // Remove extracted thinking blocks from accumulated content
                    for (const match of thinkingMatches) {
                      webSearchAccumulatedContent = webSearchAccumulatedContent.replace(match, '');
                    }
                  }
                  
                  // Check if we still have incomplete thinking tags
                  const stillHasIncompleteTags = webSearchAccumulatedContent.includes('<thinking') || 
                                                 webSearchAccumulatedContent.includes('thinking>') ||
                                                 webSearchAccumulatedContent.includes('</thinking');
                  
                  if (stillHasIncompleteTags) {
                    // Still waiting for complete thinking block, don't emit yet
                    continue;
                  }
                  
                  // If we have clean accumulated content after extracting thinking, emit it
                  if (webSearchAccumulatedContent.trim().length > 0) {
                    const cleanContent = webSearchAccumulatedContent.trim();
                    if (!webSearchContentStarted) {
                      webSearchContentStarted = true;
                      // Send marker to replace loading message
                      onChunk(JSON.stringify({ __web_search_start: true }));
                    }
                    onChunk(cleanContent);
                    webSearchAccumulatedContent = ''; // Clear buffer after emitting
                  }
                  continue;
                }
                
                // No thinking tags in this chunk - stream it directly
                const hasSubstantialContent = content.replace(/[\s.,!?;:]/g, '').length > 0;
                
                if (!webSearchContentStarted && hasSubstantialContent) {
                  webSearchContentStarted = true;
                  // First real content chunk - send a special marker to replace the loading message
                  onChunk(JSON.stringify({ __web_search_start: true }));
                }
                
                // Stream all content once we've started (including whitespace/punctuation for natural flow)
                if (webSearchContentStarted) {
                  onChunk(content);
                }
              } else if (parsed.type === 'tool_progress') {
                // Optional: could emit a status update here
              } else if (parsed.type === 'done') {
                // Log the done event to see if it contains response_id
                logger.log('[web_search][done-event]', JSON.stringify(parsed).substring(0, 500));
                
                // Capture response_id if present for session continuity
                if (parsed.response_id || parsed.id) {
                  const responseId = parsed.response_id || parsed.id;
                  logger.log('[web_search][captured-response-id]', responseId);
                  // Send as special marker so frontend can capture it
                  onChunk(JSON.stringify({ __response_id: responseId }));
                }
                
                // Stream is complete - handle any remaining accumulated content
                if (webSearchAccumulatedContent.trim().length > 0) {
                  
                  // Check for unclosed thinking tag (including malformed tags with spaces)
                  const hasOpenThinking = webSearchAccumulatedContent.includes('<thinking>');
                  const hasCloseThinking = /<\/\s*thinking\s*>/i.test(webSearchAccumulatedContent);
                  
                  if (hasOpenThinking && !hasCloseThinking) {
                    // Extract incomplete thinking content
                    const thinkingMatch = webSearchAccumulatedContent.match(/<thinking>([\s\S]*)/);
                    if (thinkingMatch) {
                      const thinkingContent = thinkingMatch[1].trim();
                      if (thinkingContent && !webSearchThinkingPieces.some(p => p.includes(thinkingContent))) {
                        onChunk(JSON.stringify({ __web_search_thinking: true, content: thinkingContent }));
                      }
                    }
                    // Clear accumulated content since we've extracted what we can
                    webSearchAccumulatedContent = '';
                  } else if (hasOpenThinking && hasCloseThinking) {
                    // Extract complete thinking and any remaining content (handle malformed tags with spaces)
                    const completeMatches = webSearchAccumulatedContent.match(/<thinking>([\s\S]*?)<\/\s*thinking\s*>/gi);
                    if (completeMatches) {
                      completeMatches.forEach(match => {
                        const thinkingContent = match.replace(/<\/?thinking\s*>/gi, '').trim();
                        if (thinkingContent && !webSearchThinkingPieces.includes(match)) {
                          onChunk(JSON.stringify({ __web_search_thinking: true, content: thinkingContent }));
                          webSearchThinkingPieces.push(match);
                        }
                      });
                      // Remove thinking blocks (including malformed ones) and emit remaining content
                      let cleanedContent = webSearchAccumulatedContent;
                      for (const match of completeMatches) {
                        cleanedContent = cleanedContent.replace(match, '');
                      }
                      webSearchAccumulatedContent = cleanedContent;
                    }
                    
                    // Emit any remaining content after cleaning
                    const remainingContent = webSearchAccumulatedContent.trim();
                    if (remainingContent.length > 0) {
                      // Start content stream
                      onChunk(JSON.stringify({ __web_search_start: true }));
                      onChunk(remainingContent);
                    }
                  } else {
                    // No thinking tags, just emit the content
                    const contentToEmit = webSearchAccumulatedContent.trim();
                    if (contentToEmit.length > 0) {
                      onChunk(JSON.stringify({ __web_search_start: true }));
                      onChunk(contentToEmit);
                    }
                  }
                }
                
                // Send web search citations as JSON marker for SourcesList component
                if (webSearchCitations.length > 0) {
                  const webSearchCitationsFiltered = Array.from(new Set(webSearchCitations.map((cit) => cit.url))).map(url => webSearchCitations.find(cit => cit.url === url)).filter(Boolean); // remove duplicates
                  // Send citations as structured data for the SourcesList component
                  const citationsPayload = {
                    __web_search_citations: true,
                    citations: webSearchCitationsFiltered.map(citation => ({
                      url: citation!.url,
                      title: citation!.domain,
                      snippet: undefined // Web search doesn't provide snippets in current implementation
                    }))
                  };
                  onChunk(JSON.stringify(citationsPayload));
                }
              }
            } catch (parseError) {
              // Parse error - skip this chunk
            }
          }
          
          // Skip generic parser - we've handled this chunk
          continue;
        }

        // Special SSE/event parsing for image_generation tool
        // Backend sends structured JSON events for progressive image updates (25%, 50%, 100%)
        // Format: data: {"type":"image_generation.partial_image","data":null,"image_data":{"b64_json":"..."}}
        // CRITICAL: Large base64 payloads span multiple TCP packets, so JSON may be incomplete
        if (tools?.includes('image_generation')) {
          imageGenerationBuffer += chunk;
          logger.log('[IMAGE-GEN][BUFFER-STATE]', {
            bufferLength: imageGenerationBuffer.length,
            bufferPreview: imageGenerationBuffer.slice(0, 150)
          });
          
          // Parse complete SSE events (separated by double newlines)
          // SSE format: "data: <json>\n\n" or "data: <json>\n\ndata: <json>\n\n"
          const eventDelimiter = /\n\n/;
          let match: RegExpExecArray | null;
          
          while ((match = eventDelimiter.exec(imageGenerationBuffer)) !== null) {
            const block = imageGenerationBuffer.slice(0, match.index).trim();
            imageGenerationBuffer = imageGenerationBuffer.slice(match.index + 2); // Skip "\n\n"
            
            if (!block) continue;
            
            // Extract data lines from the event block
            const lines = block.split('\n');
            const dataLines: string[] = [];
            
            for (const line of lines) {
              if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim()); // Remove "data:" prefix
              }
            }
            
            if (dataLines.length === 0) continue;
            
            // Join multi-line data (shouldn't happen for image_generation, but handle it)
            const jsonStr = dataLines.join('\n');
            if (!jsonStr || jsonStr === '[DONE]') continue;
            
            try {
              const parsed = JSON.parse(jsonStr);
              
              // Check for error response — two possible shapes:
              //   1. { error: { code: '...', message: '...' } }
              //   2. { type: 'error', error_data: { error: true, error_type: '...', message: '...' } }
              const errorCode: string | undefined = parsed.error?.code ?? (parsed.type === 'error' ? (parsed.error_data?.error_type ?? 'api_error') : undefined);
              const rawErrorMessage: string = parsed.error?.message ?? parsed.error_data?.message ?? '';
              // Produce a clean, user-friendly error message:
              //   - Safety rejections → concise message without the noisy request-ID tail
              //   - Other API errors  → strip "contact us / request ID" boilerplate, keep the lead sentence
              //   - Fallback          → generic server error
              const errorMessage: string = (() => {
                if (!rawErrorMessage) return 'Something went wrong or there is high load on our server. Please try again later.';
                if (/rejected.*safety/i.test(rawErrorMessage) || /safety.*rejected/i.test(rawErrorMessage)) {
                  return 'Your request was rejected by the safety system. Please try rephrasing your prompt.';
                }
                // Strip noisy "If you believe this is an error, contact us at ... request ID ..." tail
                const stripped = rawErrorMessage.replace(/\s*If you believe this is an error[^.]*\./i, '').trim();
                return stripped || rawErrorMessage;
              })();
              if (errorCode) {
                // Handle quota_exceeded error specifically
                if (errorCode === 'quota_exceeded') {
                  // Fetch quota data and create detailed error message
                  try {
                    const quotaResp = await this.getImageQuota();
                    if (quotaResp.success && quotaResp.quota) {
                      const { current_usage, quota_limit, reset_time } = quotaResp.quota;
                      
                      // Format reset time
                      let formattedResetTime = 'tomorrow';
                      try {
                        const resetDate = new Date(reset_time);
                        formattedResetTime = resetDate.toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        });
                      } catch {
                        formattedResetTime = 'tomorrow';
                      }
                      
                      throw new Error(`Image limit reached. You generated ${current_usage}/${quota_limit} images for the day. Your limit will reset at ${formattedResetTime}.`);
                    }
                  } catch (quotaError) {
                    // If quota fetch fails, use the error message from API
                    if (quotaError instanceof Error && quotaError.message.startsWith('Image limit reached')) {
                      throw quotaError;
                    }
                  }
                  
                  // Fallback to generic quota error message
                  throw new Error(errorMessage || 'Image generation quota exceeded. Please try again later.');
                }
                
                throw new Error(errorMessage);
              }
              
              // Handle partial and completed image events
              if ((parsed.type === 'image_generation.partial_image' || parsed.type === 'image_generation.completed') 
                  && parsed.image_data?.b64_json) {
                const base64 = parsed.image_data.b64_json;
                const imageMarkdown = `![Generated Image](data:image/png;base64,${base64})`;
                
                logger.log('[IMAGE-GENERATION-EVENT]', {
                  type: parsed.type,
                  base64Length: base64.length,
                  isComplete: parsed.type === 'image_generation.completed'
                });
                
                // Send as replacement to avoid accumulation
                onChunk(JSON.stringify({
                  type: 'replace',
                  content: imageMarkdown
                }));
              }
            } catch (parseError) {
              // If it's our thrown error (from the errorCode block or quota logic), re-throw it
              // We distinguish intentional errors from JSON parse errors by checking if the error
              // was constructed with a non-empty message (all our throws use non-empty strings).
              if (parseError instanceof Error && parseError.message && !(parseError instanceof SyntaxError)) {
                throw parseError;
              }
              // Otherwise it's a JSON parse error - log but keep trying
              console.warn('[IMAGE-GENERATION-PARSE-ERROR]', { 
                jsonStrPreview: jsonStr.slice(0, 100),
                error: parseError 
              });
            }
          }
          
          // Skip generic parser - we've handled this chunk
          continue;
        }

        // Line-based reasoning parsing (non-blocking)
        // Essentially parses line by line, with explicit handling for event: reasoning because
        // reasoning can have multile "data:" lines within one event so needs that second line to be added to the text
        // One thing I noticed too is that we receive random lines that are not labeled as Chain of Thought in the middle
        // of receiving the CoT chunks. They're normally random words that aren't meant to be part of the content. I handle
        // this in useChatStream) by checking if the chunk has the __reasoning flag and if so, appending it to the last content chunk instead of treating it as a separate chunk. 
        // This is a bit hacky but it works given the current SSE format. If we had more control over the backend, 
        // we could add explicit markers for reasoning content vs non-reasoning 
        // content within the reasoning event. Or we could ask if backend team can fix the leaking. (TODO)
        // REASONING STREAMING HANDLING
        if (isReasoning) {
          const rawLines = chunk.split(/\r?\n/);
          const chunkEndsWithNewline = /\r?\n$/.test(chunk);
          for (let i = 0; i < rawLines.length; i++) {
            // Prepend any carry-over from a previous chunk that ended mid-line
            const line = i === 0 && reasoningLineCarryOver
              ? reasoningLineCarryOver + rawLines[i]
              : rawLines[i];
            if (i === 0) reasoningLineCarryOver = '';
            // Last element from split: if the original chunk did NOT end with \n, the last
            // element is an incomplete line split across a network boundary.
            // Save it as carry-over so the next chunk's bytes complete it.
            const isLastElement = i === rawLines.length - 1;
            if (isLastElement && !chunkEndsWithNewline && line &&
                (line.startsWith('data:') || line.startsWith('event:'))) {
              reasoningLineCarryOver = line;
              break;
            }
            if (!line) {
              // Blank line signals end of reasoning event if open
              if (reasoningEventOpen && reasoningLines.length) {
                // Each entry in reasoningLines is a separate JSON object (one per data: line).
                // Parse each line individually and concatenate the content pieces so that
                // multi-line reasoning events are assembled correctly instead of falling back
                // to the raw joined JSON strings (which caused raw JSON to appear in the logs).
                let reasoningContent = '';
                for (const dataLine of reasoningLines) {
                  if (!dataLine) continue;
                  try {
                    const parsed = JSON.parse(dataLine);

                    // Check for error response — two shapes:
                    //   Shape 1: { error: { code, message } }
                    //   Shape 2: { type: 'error', error_data: { error_type, message } }
                    const errCode: string | undefined =
                      parsed?.error?.code ??
                      (parsed?.type === 'error' ? (parsed?.error_data?.error_type ?? 'api_error') : undefined);
                    if (errCode) {
                      // Handle quota_exceeded error specifically
                      if (errCode === 'quota_exceeded') {
                        // Fetch quota data and create detailed error message
                        try {
                          const quotaResp = await this.getImageQuota();
                          if (quotaResp.success && quotaResp.quota) {
                            const { current_usage, quota_limit, reset_time } = quotaResp.quota;
                            
                            // Format reset time
                            let formattedResetTime = 'tomorrow';
                            try {
                              const resetDate = new Date(reset_time);
                              formattedResetTime = resetDate.toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              });
                            } catch {
                              formattedResetTime = 'tomorrow';
                            }
                            
                            throw new Error(`Image limit reached. You generated ${current_usage}/${quota_limit} images for the day. Your limit will reset at ${formattedResetTime}.`);
                          }
                        } catch (quotaError) {
                          // If quota fetch fails, use the error message from API
                          if (quotaError instanceof Error && quotaError.message.startsWith('Image limit reached')) {
                            throw quotaError;
                          }
                        }
                        
                        // Fallback to generic quota error message
                        throw new Error(parsed.error.message || 'Image generation quota exceeded. Please try again later.');
                      }
                      
                      // For all other server errors, surface the message verbatim
                      const rawMsg: string = parsed?.error?.message ?? parsed?.error_data?.message ?? '';
                      const errMsg = rawMsg || 'Something went wrong or there is high load on our server. Please try again later.';
                      const serverError = new Error(errMsg);
                      (serverError as any).isServerError = true;
                      throw serverError;
                    }

                    const piece: string = parsed?.data?.content ?? parsed?.content ?? '';
                    reasoningContent += piece;
                  } catch (parseError) {
                    // Re-throw intentional errors (server errors, quota errors, etc.)
                    if (parseError instanceof Error && !(parseError instanceof SyntaxError)) {
                      throw parseError;
                    }
                    // JSON parse failure for this individual line – skip it silently
                  }
                }
                // Only emit if we extracted actual content
                if (reasoningContent) {
                  onChunk(JSON.stringify({ __reasoning: true, content: reasoningContent }));
                }
                // Do NOT merge into normal stream here; keep answer content separate
              }
              reasoningEventOpen = false;
              reasoningLines = [];
              continue;
            }
            if (line.startsWith('event:')) {
              const evt = line.slice(6).trim();
              const wasReasoning = reasoningEventOpen;
              reasoningEventOpen = evt === 'reasoning';
              if (wasReasoning && !reasoningEventOpen) {
                // Reasoning phase just ended - reset accumulated state so leftover
                // reasoning-phase data lines don't corrupt the content delta.
                accumulatedText = '';
                filterStreamContent('', true);
              }
              continue;
            }
            if (line.startsWith('data:')) {
              // Preserve full 'data: ' prefix so filterStreamContent can split properly
              if (reasoningEventOpen) {
                // Strip prefix for reasoning buffer JSON clarity
                const dataPayload = line.slice(5).trim();
                reasoningLines.push(dataPayload);
              } else {
                // For the answer-content phase, extract the delta directly from each SSE line
                // instead of relying on filterStreamContent's processedContent delta tracker.
                // The delta tracker is error-prone: if any call returns '' (e.g. due to the
                // content===processedContent early-exit), subsequent deltas are computed against
                // a stale baseline and the skipped tokens (e.g. " rep", "eat") are silently lost.
                const dataPayload = line.slice(5).trim();
                if (dataPayload && dataPayload !== '[DONE]') {
                  try {
                    const parsed = JSON.parse(dataPayload);

                    // Check for error response — two shapes:
                    //   Shape 1: { error: { code, message } }
                    //   Shape 2: { type: 'error', error_data: { error_type, message } }
                    const errCode: string | undefined =
                      parsed?.error?.code ??
                      (parsed?.type === 'error' ? (parsed?.error_data?.error_type ?? 'api_error') : undefined);
                    if (errCode) {
                      const rawMsg: string = parsed?.error?.message ?? parsed?.error_data?.message ?? '';
                      const errMsg = rawMsg || 'Something went wrong or there is high load on our server. Please try again later.';
                      const serverError = new Error(errMsg);
                      (serverError as any).isServerError = true;
                      throw serverError;
                    }

                    // Extract text delta — match filterStreamContent's exact extraction logic
                    const delta: string =
                      (parsed?.type === 'content' && parsed?.data?.content)
                        ? parsed.data.content
                        : (parsed?.choices?.[0]?.delta?.content ?? '');
                    if (delta) {
                      accumulatedText += delta; // keep accumulating for stream-done flush
                      onChunk(delta);
                    }
                    // Intercept followups in reasoning path
                    if (parsed?.type === 'followups' && parsed?.followups_data?.followups) {
                      onChunk(JSON.stringify({ __followups: true, followups: parsed.followups_data.followups }));
                    }
                    // If parsed but no delta (e.g. metadata-only line), silently skip.
                  } catch (parseError) {
                    // Re-throw intentional errors (server errors, quota errors, etc.)
                    if (parseError instanceof Error && !(parseError instanceof SyntaxError)) {
                      throw parseError;
                    }
                    // JSON parse failed — the line is likely split across a chunk boundary.
                    // Save the full line (with 'data:' prefix) as carry-over so the next
                    // chunk's first bytes complete it and the combined string can be parsed.
                    reasoningLineCarryOver = line;
                  }
                }
              }
              continue;
            }
            // Fallback: line without event/data prefix; treat as raw content
            accumulatedText += line + '\n';
            const contentPiece = filterStreamContent(accumulatedText);
            if (contentPiece) onChunk(contentPiece);
          }
          // Skip generic path; we've already processed lines
          continue;
        }

        // Generic accumulation path (non data_analysis, non reasoning)
        // Intercept followups SSE chunks before accumulation so filterStreamContent doesn't discard them.
        // Scan across the tail of accumulatedText + current chunk so SSE JSON split across
        // network boundaries can still be parsed and removed before rendering.
        {
          const findJsonObjectEnd = (text: string, startIndex: number): number => {
            let depth = 0;
            let inString = false;
            let escaped = false;

            for (let i = startIndex; i < text.length; i++) {
              const char = text[i];

              if (inString) {
                if (escaped) {
                  escaped = false;
                } else if (char === '\\') {
                  escaped = true;
                } else if (char === '"') {
                  inString = false;
                }
                continue;
              }

              if (char === '"') {
                inString = true;
              } else if (char === '{') {
                depth++;
              } else if (char === '}') {
                depth--;
                if (depth === 0) {
                  return i + 1;
                }
              }
            }

            return -1;
          };

          const applyRemovals = (text: string, removals: Array<{ start: number; end: number }>): string => {
            if (removals.length === 0) {
              return text;
            }

            let result = '';
            let cursor = 0;
            for (const removal of removals) {
              result += text.slice(cursor, removal.start);
              cursor = removal.end;
            }
            result += text.slice(cursor);
            return result;
          };

          const followupsExtracted: string[] = [];
          const historyWindowSize = 8192;
          const historyTail = accumulatedText.slice(-historyWindowSize);
          const historyPrefixLength = accumulatedText.length - historyTail.length;
          const combinedBuffer = historyTail + chunk;
          const removals: Array<{ start: number; end: number }> = [];

          let searchIndex = 0;
          while (searchIndex < combinedBuffer.length) {
            const dataIndex = combinedBuffer.indexOf('data:', searchIndex);
            if (dataIndex === -1) {
              break;
            }

            let jsonStart = dataIndex + 5;
            while (jsonStart < combinedBuffer.length && /\s/.test(combinedBuffer[jsonStart])) {
              jsonStart++;
            }

            if (combinedBuffer[jsonStart] !== '{') {
              searchIndex = dataIndex + 5;
              continue;
            }

            const jsonEnd = findJsonObjectEnd(combinedBuffer, jsonStart);
            if (jsonEnd === -1) {
              break;
            }

            const jsonText = combinedBuffer.slice(jsonStart, jsonEnd);
            try {
              const parsed = JSON.parse(jsonText);
              if (parsed?.type === 'followups' && Array.isArray(parsed?.followups_data?.followups)) {
                followupsExtracted.push(...parsed.followups_data.followups);

                let removalEnd = jsonEnd;
                while (
                  removalEnd < combinedBuffer.length &&
                  (combinedBuffer[removalEnd] === '\r' || combinedBuffer[removalEnd] === '\n')
                ) {
                  removalEnd++;
                }

                removals.push({ start: dataIndex, end: removalEnd });
                searchIndex = removalEnd;
                continue;
              }
            } catch {
              // Invalid JSON despite balanced braces; leave untouched.
            }

            searchIndex = jsonEnd;
          }

          if (followupsExtracted.length > 0) {
            const sanitizedCombinedBuffer = applyRemovals(combinedBuffer, removals);
            const removedBeforeChunk = removals.reduce((total, removal) => {
              return total + Math.max(0, Math.min(removal.end, historyTail.length) - Math.min(removal.start, historyTail.length));
            }, 0);
            const sanitizedHistoryLength = historyTail.length - removedBeforeChunk;

            accumulatedText =
              accumulatedText.slice(0, historyPrefixLength) +
              sanitizedCombinedBuffer.slice(0, sanitizedHistoryLength);
            chunk = sanitizedCombinedBuffer.slice(sanitizedHistoryLength);

            onChunk(JSON.stringify({ __followups: true, followups: followupsExtracted }));
          }
        }
        accumulatedText += chunk;
        
        // CRITICAL: Error detection for 200 OK responses with error payloads
        // Accumulate chunks to detect complete error JSON (errors might span multiple chunks)
        errorCheckBuffer += chunk;
        
        // Try to find complete error JSON in the buffer
        // Look for the pattern: data: {...type":"error"...}
        const dataPrefix = errorCheckBuffer.lastIndexOf('data:');
        if (dataPrefix !== -1) {
          const afterData = errorCheckBuffer.slice(dataPrefix + 5).trim();
          // Try to parse if it starts with {
          if (afterData.startsWith('{')) {
            // Find the matching closing brace
            let braceCount = 0;
            let jsonEnd = -1;
            for (let i = 0; i < afterData.length; i++) {
              if (afterData[i] === '{') braceCount++;
              else if (afterData[i] === '}') {
                braceCount--;
                if (braceCount === 0) {
                  jsonEnd = i + 1;
                  break;
                }
              }
            }
            
            if (jsonEnd > 0) {
              const jsonString = afterData.slice(0, jsonEnd);
              try {
                const parsed = JSON.parse(jsonString);

                // The server emits errors in two shapes on a 200 OK SSE stream:
                //   1. { error: { code, message } }
                //   2. { type: 'error', status_code, error_data: { error_type, message } }
                // Detect both, and prefer the real server-provided message verbatim
                // so the user sees the actual cause (e.g. DeploymentNotFound 404)
                // instead of a guessed/generic fallback.
                const isErrorEvent =
                  (parsed?.error && parsed.error.code) ||
                  (parsed?.type === 'error' && parsed?.error_data);

                if (isErrorEvent) {
                  const errorCode: string | undefined =
                    parsed?.error?.code ?? parsed?.error_data?.error_type;
                  const rawErrorMessage: string =
                    parsed?.error?.message ?? parsed?.error_data?.message ?? '';

                  // Handle quota_exceeded specifically (preserves existing UX)
                  if (errorCode === 'quota_exceeded') {
                    try {
                      const quotaResp = await this.getImageQuota();
                      if (quotaResp.success && quotaResp.quota) {
                        const { current_usage, quota_limit, reset_time } = quotaResp.quota;

                        let formattedResetTime = 'tomorrow';
                        try {
                          const resetDate = new Date(reset_time);
                          formattedResetTime = resetDate.toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          });
                        } catch {
                          formattedResetTime = 'tomorrow';
                        }

                        throw new Error(`Image limit reached. You generated ${current_usage}/${quota_limit} images for the day. Your limit will reset at ${formattedResetTime}.`);
                      }
                    } catch (quotaError) {
                      if (quotaError instanceof Error && quotaError.message.startsWith('Image limit reached')) {
                        throw quotaError;
                      }
                    }

                    throw new Error(rawErrorMessage || 'Image generation quota exceeded. Please try again later.');
                  }

                  // For all other errors, surface the server-provided message verbatim
                  // so the user sees the real cause instead of a generic guess.
                  throw new Error(rawErrorMessage || 'Something went wrong or there is high load on our server. Please try again later.');
                }
              } catch (parseError) {
                // Re-throw any Error we constructed above so the outer catch handles it
                if (parseError instanceof Error && parseError.message && !(parseError instanceof SyntaxError)) {
                  throw parseError;
                }
                // Otherwise it's a JSON parse error - continue accumulating
              }
            }
          }
        }
        
        // Keep buffer size reasonable (last 2000 chars should be enough to catch errors)
        if (errorCheckBuffer.length > 2000) {
          errorCheckBuffer = errorCheckBuffer.slice(-2000);
        }
        
        const content = filterStreamContent(accumulatedText);
        
        if (content) {
          if (tools && tools.includes('image_generation')) {
            // For image generation, if we get a full image markdown, mark it as a replacement
            if (content.includes('![Generated Image](data:image/png;base64,')) {
              // Check if we have a reasonably complete image (at least 500KB base64)
              const base64Match = content.match(/data:image\/png;base64,([A-Za-z0-9+/=]+)\)/);
              const base64Length = base64Match ? base64Match[1].length : 0;
                            
              // Only send replacement if we have a substantial image (minimum 500KB)
              if (base64Length >= 500000) {
                // Create a special replacement object to indicate this should replace the entire message
                onChunk(JSON.stringify({
                  type: 'replace',
                  content: content
                }));
              } else {
                onChunk(content);
              }
            } else {
              onChunk(content);
            }
          } else {
            onChunk(content);
          }
          if (ChatService.isDebug()) {
            // Keep small rolling preview (avoid large memory usage)
            if (content.includes('data:image/png;base64,')) {
              debugResponsePieces.push('[IMAGE_DATA]');
            } else {
              debugResponsePieces.push(content.slice(0, 500));
            }
            // Cap accumulated preview length (~8000 chars raw before sanitization)
            const joined = debugResponsePieces.join('');
            if (joined.length > 8000) {
              // Remove oldest entries until under threshold
              while (debugResponsePieces.join('').length > 6000) {
                debugResponsePieces.shift();
              }
            }
          }
        }
      }

    } catch (error: any) {
      // Don't log AbortError as it's expected when user stops the stream
      if (error.name !== 'AbortError') {
        // Error occurred
        logger.error('Error:', error?.message);
      }

      // Detect browser-level CORS / network failure (TypeError thrown before any HTTP
      // response is received — e.g. "Failed to fetch", "NetworkError when attempting
      // to fetch resource", "Load failed").
      const isCorsOrNetwork =
        error instanceof TypeError &&
        /(failed to fetch|networkerror|load failed)/i.test(error.message);
      if (isCorsOrNetwork) {
        const corsError = new Error(
          'Your request could not be completed. Please try again or try rephrasing your prompt. ' +
          'If this keeps happening, please contact support.'
        );
        onError(corsError);
        return;
      }
      
      // Don't retry quota errors, safety rejections, abort errors, or server content errors
      const isApiOriginatedError = error instanceof Error && (
        (error as any).isServerError === true ||
        error.message.includes('Image limit reached') ||
        error.message.includes('safety system') ||
        error.message.includes('rejected') ||
        error.message.includes('quota exceeded')
      );
      const shouldRetry = error.name !== 'AbortError' && !isApiOriginatedError && retryCount < ChatService.MAX_RETRIES;
            
      // Retry logic for network errors, as long as we've retried less than 3 times and the user didn't purposely abort it, retry again on error
      if (shouldRetry) {
        await this.delay(this.getRetryDelay(retryCount));
        
        if (signal?.aborted) {
          throw new Error('Request aborted');
        }
        
  return this.streamChatCompletion(chatHistory, onChunk, onError, onComplete, signal, retryCount + 1, tools, sessionId, files, continueLlmResponse, model, queryTypeOverride);
      }
      
      // Preserve API-originated error messages; use generic only for unexpected network/parse errors
      let finalError = error;
      if (error.name !== 'AbortError' && !isApiOriginatedError) {
        finalError = new Error('Something went wrong or there is high load on our server. Please try again later.');
      }
      
      onError(finalError instanceof Error ? finalError : new Error('Something went wrong or there is high load on our server. Please try again later.'));
    }
    // After exiting try/catch (success path only), if reasoning tool active and we have accumulated chunks, log consolidated output.
    // (Reasoning final consolidation now handled client-side via explicit __reasoning messages.)
  }

  /**
   * Send a single chat message and get streaming response
   */
  async sendMessage(
    message: string,
    chatHistory: ChatMessage[] = [],
    tools: string[] | undefined,
    onChunk: (chunk: string) => void,
    onError: (error: Error) => void,
    onComplete: () => void,
    signal?: AbortSignal,
    sessionId?: string,
    files?: Array<{ filename: string; content_base64?: string; file_id?: string }>,
    continueLlmResponse?: boolean,
    model?: string,
    queryTypeOverride?: string,
    ragType?: 'la' | 'hr'
  ): Promise<void> {
    // Use chatHistory as-is - it already includes the user message with structured content (files + text)
    // from useChatStream.ts. Don't append message again or it will overwrite the structured format.
    return this.streamChatCompletion(
      chatHistory,
      onChunk,
      onError,
      onComplete,
      signal,
      0,
      tools,
      sessionId,
      files,
      continueLlmResponse,
      model,
      queryTypeOverride,
      ragType
    );
  }
  /**
   * Download generated analysis file by container + file id, returns Blob.
   */
  async downloadAnalysisFile(containerId: string, fileId: string): Promise<Blob> {
    const accessToken = await this.getAccessToken();
    const endpoint = `${ChatService.API_BASE_URL}/file-download`;
    const resp = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({ container_id: containerId, file_id: fileId })
    });
    if (!resp.ok) {
      throw new Error(`File download failed (${resp.status})`);
    }
    return await resp.blob();
  }
}

// Export a singleton instance factory
export const createChatService = (msalInstance: IPublicClientApplication): ChatService => {
  return new ChatService(msalInstance);
};